/**
 * The Abstract Store's configuration, as the `/Store` pages see it.
 *
 * Two layers, from two servers:
 *
 * 1. **Where the Store is.** AbstractID's own `/api/configuration` publishes
 *    `storeBackendURL` (and the Store's application UUID). It is published at
 *    runtime because the client image is built once for every environment.
 *    `null` means no store is configured, and the storefront is hidden.
 * 2. **What the Store says.** The Store's own `/api/configuration` — currencies,
 *    whether it is open, and answers about the caller (suggested currency, the
 *    billing identity on file). Loaded on first entry to a `/Store` page, and
 *    re-read after an inline sign-in, since those answers change with the caller.
 */

import { derived, get, writable } from 'svelte/store';
import { configurationStore } from '../AppConfiguration';
import type { StoreConfiguration } from './StoreTypes';

/** The Store backend's origin, or `''` when no store is configured. */
export const storeBackendOrigin = derived(configurationStore, ($config) =>
	($config?.storeBackendURL ?? '').replace(/\/+$/, '')
);

/** Whether this deployment has a store at all — what shows the `/Store` sidebar rows. */
export const isStoreEnabled = derived(storeBackendOrigin, ($origin) => $origin.length > 0);

/** The Store's application UUID in this registry, or `''` when none is configured. */
export const storeAppUUID = derived(configurationStore, ($config) => $config?.storeAppUUID ?? '');

/**
 * The Store backend's origin, for building a request URL.
 *
 * Throws rather than answering `''`: a relative URL would reach *this* server,
 * which answers an unknown API path with a 404 the page would then report as a
 * store fault — the real fault being that no store is configured at all.
 */
export function storeBackendURL(): string {
	const origin = get(storeBackendOrigin);
	/* No store configured — refuse before building a URL that points at the wrong server. */
	if (!origin) throw new Error('The store is not available.');
	return origin;
}

/** The Store's own configuration. `null` until the first successful load. */
export const storeConfigurationStore = writable<StoreConfiguration | null>(null);

/** The message from the last failed load, or `null`. */
export const storeConfigurationError = writable<string | null>(null);

/** Whether the Store's configuration has been loaded. */
export const isStoreConfigurationLoaded = derived(storeConfigurationStore, ($config) => $config !== null);

/** The load in flight, shared so concurrent callers make one request. */
let pending: Promise<void> | null = null;

/**
 * Loads the Store's configuration once; later calls reuse it.
 *
 * What a `/Store` route waits on before it mounts, because every storefront
 * page formats money from the currency rows this carries. A failure is kept
 * in `storeConfigurationError` rather than thrown, so the shell can say the
 * store is unreachable instead of rendering pages with no currencies.
 */
export function ensureStoreConfiguration(): Promise<void> {
	/* Already loaded — nothing to wait for. */
	if (get(storeConfigurationStore) !== null) return Promise.resolve();
	pending ??= loadStoreConfiguration().finally(() => {
		pending = null;
	});
	return pending;
}

/**
 * Re-reads the Store's configuration for **this** caller.
 *
 * `suggestedCurrency`, `billingCountryRequired` and the billing identity on
 * file are answers about whoever is asking, and an inline sign-in changes who
 * that is without a page load to refresh them.
 */
export async function loadStoreConfiguration(): Promise<void> {
	/* Imported lazily: the network module itself reads this file for its origin. */
	const { getStoreConfiguration } = await import('./StoreNetwork');
	/* A failure is recorded for the shell to report, never thrown into a route guard. */
	try {
		storeConfigurationStore.set(await getStoreConfiguration());
		storeConfigurationError.set(null);
	} catch (error) {
		storeConfigurationError.set(error instanceof Error ? error.message : 'Failed to load the store');
	}
}
